/* eslint-disable jsx-a11y/media-has-caption */

import React, { useEffect, useState } from "react";
import { ActionButton, DateTimeField, Form, Loader, ModalDialog, TextField, TimezoneField } from "@culturehq/components";
import { makePatch } from "@culturehq/client";

import { useProfile } from "app/context";
import { useDoor } from "app/hooks";
import { t, Trans } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";
import SocialMediaPreviewCard from "./SocialMediaPreviewCard";

const FormData = ({
  handleScheduledAtChange,
  onMessageChanged,
  shareMessage,
  scheduledAt
}) => {
  const [timezoneOffset, setTimezoneOffset] = useState(-new Date().getTimezoneOffset());

  const handleOffsetChange = timezone => {
    setTimezoneOffset(timezone);
  };

  return (
    <div className="Form-Data">
      <TextField
        className="textarea"
        onChange={onMessageChanged}
        placeholder={t("Put your message here...")}
        value={shareMessage}
      />
      {scheduledAt && (
        <div className="row">
          <div className="col-sm-6">
            <DateTimeField
              ariaLabel={t("Schedule the post for later")}
              name="scheduledAt"
              offset={timezoneOffset}
              value={scheduledAt}
              onChange={handleScheduledAtChange}
              required
            >
              <Trans>Schedule for</Trans>
            </DateTimeField>
          </div>
          <div className="col-sm-6">
            <Form initialValues={{ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }}>
              <TimezoneField
                name="timezone"
                onOffsetChange={handleOffsetChange}
              >
                <Trans>Event Time Zone</Trans>
              </TimezoneField>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

const EditApprovedMessageModal = ({
  approvedContentMessage, children, onUpdateApprovedForSocialMessage, story
}) => {
  const { profile } = useProfile();
  const { open, onOpen, onClose } = useDoor(false);
  const [shareMessage, setShareMessage] = useState();
  const [loading, setLoading] = useState(false);
  const [scheduledAt, setScheduledAt] = useState();

  useEffect(
    () => {
      if (approvedContentMessage?.message) {
        setShareMessage(approvedContentMessage.message);
      } else {
        setShareMessage(story.body ? `${JSON.parse(story.body).blocks.map(block => block.text).join("\n")}` : "");
      }

      if (approvedContentMessage?.scheduledAt) {
        setScheduledAt(approvedContentMessage.scheduledAt);
      } else {
        setScheduledAt();
      }
    }, [approvedContentMessage, story]
  );

  const onMessageChanged = text => {
    setShareMessage(text);
  };

  const handleUpdate = async () => {
    setLoading(true);

    const body = {
      message: shareMessage,
      scheduledAt
    };

    const { approvedForSocialMessage } = await makePatch(`/stories/approved/message/${approvedContentMessage.id}`, body);
    if (onUpdateApprovedForSocialMessage) {
      onUpdateApprovedForSocialMessage(approvedForSocialMessage);
    }

    setLoading(false);
    onClose();
  };

  const handleScheduledAtChange = newScheduledAt => {
    setScheduledAt(newScheduledAt);
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog
          className="ApproveForSocial EditApprovedMessage"
          onClose={onClose}
        >
          <ModalDialog.Heading onClose={onClose}>
            <Trans>Edit Approved Story Message</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="col-sm-6 Input">
              <FormData
                handleScheduledAtChange={handleScheduledAtChange}
                shareMessage={shareMessage}
                onMessageChanged={onMessageChanged}
                scheduledAt={scheduledAt}
              />
            </div>
            <div className="col-sm-6 Preview no-scrollbar">
              <h4 className="Preview-Title"><Trans>Live Preview</Trans></h4>
              <SocialMediaPreviewCard
                category={approvedContentMessage.socialType}
                profile={profile}
                shareMessage={shareMessage}
                story={story}
              />
            </div>
            {loading && <Loader loading />}
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <div>
              <ActionButton disabled={loading} onClick={handleUpdate}>
                <Trans>Save Changes</Trans>
              </ActionButton>
            </div>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
    </>
  );
};

export default EditApprovedMessageModal;
